import { useState, useCallback } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { downloadUint8Array } from '../../utils/fileUtils';
import { useUIStore } from '../../stores/uiStore';
import { PDFDocument } from 'pdf-lib';
import './Dialogs.css';

interface CompressDialogProps {
  onClose: () => void;
}

export function CompressDialog({ onClose }: CompressDialogProps) {
  const { documents, activeDocumentId } = useDocumentStore();
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const [isCompressing, setIsCompressing] = useState(false);
  const [result, setResult] = useState<{ original: number; compressed: number } | null>(null);

  const handleCompress = useCallback(async () => {
    if (!activeDoc?.arrayBuffer) return;
    setIsCompressing(true);

    try {
      // Load and re-save with compression options
      const pdf = await PDFDocument.load(activeDoc.arrayBuffer);

      // Remove metadata to reduce size
      pdf.setTitle('');
      pdf.setAuthor('');
      pdf.setSubject('');
      pdf.setKeywords([]);
      pdf.setCreator('PDF Exchange Pro');

      const compressed = await pdf.save({
        useObjectStreams: true,
        addDefaultPage: false,
      });

      const originalSize = activeDoc.fileSize;
      const compressedSize = compressed.length;

      setResult({ original: originalSize, compressed: compressedSize });

      if (compressedSize < originalSize) {
        downloadUint8Array(compressed, `${activeDoc.filename.replace('.pdf', '')}_compressed.pdf`);
        useUIStore.getState().addToast({
          type: 'success',
          message: `Compressed! Saved ${Math.round(((originalSize - compressedSize) / originalSize) * 100)}%`,
        });
      } else {
        useUIStore.getState().addToast({
          type: 'info',
          message: 'File is already optimized, no further compression possible',
        });
      }
    } catch (err) {
      console.error('Compress failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: 'Compression failed' });
    } finally {
      setIsCompressing(false);
    }
  }, [activeDoc]);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
        <div className="modal-header">
          <h3>📦 Compress PDF</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p className="dialog-description">
            Optimize "{activeDoc?.filename}" to reduce file size.
          </p>

          {!result ? (
            <div className="compress-info">
              <div className="compress-stat">
                <span className="compress-stat-label">Current size</span>
                <span className="compress-stat-value">{formatSize(activeDoc?.fileSize || 0)}</span>
              </div>
            </div>
          ) : (
            <div className="compress-results">
              <div className="compress-stat">
                <span className="compress-stat-label">Original</span>
                <span className="compress-stat-value">{formatSize(result.original)}</span>
              </div>
              <span className="compress-arrow">→</span>
              <div className="compress-stat">
                <span className="compress-stat-label">Compressed</span>
                <span className="compress-stat-value compress-success">{formatSize(result.compressed)}</span>
              </div>
              {result.compressed < result.original && (
                <div className="compress-savings">
                  <span className="badge badge-success">
                    -{Math.round(((result.original - result.compressed) / result.original) * 100)}% saved
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {!result && (
            <button className="btn btn-primary" onClick={handleCompress} disabled={isCompressing}>
              {isCompressing ? '⏳ Compressing...' : 'Compress'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
